import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { CalendarRange, Package, RefreshCw, Truck, X } from "lucide-react"
import type { AppShell } from "@/App"
import {
  dispatchOrders,
  errorMessage,
  listOrders,
  listVehicles,
  listWarehouses,
  resendSage,
  type DispatchTarget,
  type Order,
  type Vehicle,
  type Warehouse,
} from "@/lib/api"
import { day10, dm, eur, kg, n, todayLisbon } from "@/lib/format"
import { AsyncButton, ErrorBox, inputCls, Loading, SearchSelect, Tag } from "@/components/kit"
import { Button } from "@/components/ui/button"
import { isOpen, OrderDetail } from "@/pages/Orders"
import { DispatchMap, type MapPin } from "@/pages/DispatchMap"
import { cn } from "@/lib/utils"

/** Cores dos destinos (veículos e transportadoras pela ordem em que aparecem). */
const VEHICLE_COLORS = ["#2563eb", "#0d9488", "#c026d3", "#0891b2", "#4f46e5", "#be123c"]
const CARRIER_COLORS = ["#d97706", "#7c3aed", "#dc2626", "#ca8a04", "#9333ea", "#ea580c"]
const NONE_COLOR = "#475569"
/** Pino com destino atribuído (frota ou transportadora): verde; o contorno diz qual. */
const ASSIGNED_COLOR = "#16a34a"
const FLEET_COLOR = "#334155"

type Filter = "open" | "fleet" | "carrier" | "all"
const FILTERS: Array<[Filter, string]> = [["open", "Por atribuir"], ["fleet", "Frota"], ["carrier", "Transportadora"], ["all", "Todas"]]

interface ServiceRef { id: string; carrier_id: string; label: string; color: string }

export function SageTag({ o }: { o: Order }) {
  if (!o.sage_status) return null
  if (o.sage_status === "sent") return <Tag tone="ok" title={o.sage_synced_at ? `SAGE avisado ${new Date(o.sage_synced_at).toLocaleString("pt-PT")}` : undefined}>SAGE ✓</Tag>
  if (o.sage_status === "no_flow") return <Tag tone="warn" title={o.sage_error ?? undefined}>SAGE sem fluxo</Tag>
  return <Tag tone="bad" title={o.sage_error ?? undefined}>SAGE falhou</Tag>
}

/**
 * Despacho (despacho manual, 27/10): todas as encomendas em aberto no mapa; o
 * gestor abre uma (pino ou lista), vê as opções com custo e escolhe a
 * transportadora ou a frota (veículo opcional). Cada atribuição avisa o SAGE
 * (evento → Fluxo → n8n). O dia de cada uma decide-se em «Planear o dia».
 */
export function Dispatch({ shell }: { shell: AppShell }) {
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>("open")
  const [q, setQ] = useState("")
  const [district, setDistrict] = useState("")
  const [municipality, setMunicipality] = useState("")
  const [selected, setSelected] = useState<string | null>(null)
  const [multi, setMulti] = useState<Set<string>>(new Set())
  const [bulkTarget, setBulkTarget] = useState("best")
  const [busy, setBusy] = useState<string | null>(null)
  const [detailKey, setDetailKey] = useState(0)
  const canEdit = shell.role >= 5
  const today = todayLisbon()

  const load = useCallback(async () => {
    try {
      const [o, v, w] = await Promise.all([listOrders(shell), listVehicles(shell), listWarehouses(shell)])
      setOrders(o)
      setVehicles(v.filter((x) => x.active !== false))
      setWarehouse(w.find((x) => x.active !== false) ?? null)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])
  // Esc fecha o painel da encomenda (o mapa volta a ocupar o espaço todo).
  useEffect(() => {
    if (!selected) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) setSelected(null)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [selected])

  const open = useMemo(() => (orders ?? []).filter(isOpen), [orders])
  const vehicleColor = useMemo(() => new Map(vehicles.map((v, i) => [v.id, VEHICLE_COLORS[i % VEHICLE_COLORS.length]])), [vehicles])
  // Serviços de transportadora que aparecem nas opções das encomendas (para o lote e as cores).
  const services = useMemo(() => {
    const out = new Map<string, ServiceRef>()
    const carrierColor = new Map<string, string>()
    for (const o of open) {
      for (const x of o.options) {
        if (!carrierColor.has(x.carrier_id)) carrierColor.set(x.carrier_id, CARRIER_COLORS[carrierColor.size % CARRIER_COLORS.length])
        if (!out.has(x.service_id)) out.set(x.service_id, { id: x.service_id, carrier_id: x.carrier_id, label: `${x.carrier_name} · ${x.service_name}`, color: carrierColor.get(x.carrier_id)! })
      }
    }
    return out
  }, [open])
  const colorOf = useCallback((o: Order) => {
    if (o.dispatch_kind === "fleet") return o.dispatch_vehicle_id ? vehicleColor.get(o.dispatch_vehicle_id) ?? FLEET_COLOR : FLEET_COLOR
    if (o.dispatch_kind === "carrier") return services.get(o.carrier_service_id ?? "")?.color ?? CARRIER_COLORS[0]
    return NONE_COLOR
  }, [services, vehicleColor])
  const destLabel = useCallback((o: Order) => {
    if (o.dispatch_kind === "fleet") return o.dispatch_vehicle_id ? vehicles.find((v) => v.id === o.dispatch_vehicle_id)?.name ?? "Frota" : "Frota (veículo no dia)"
    if (o.dispatch_kind === "carrier") return services.get(o.carrier_service_id ?? "")?.label ?? "Transportadora"
    return null
  }, [services, vehicles])

  const districts = useMemo(() => [...new Set(open.map((o) => o.district).filter((x): x is string => !!x))].sort(), [open])
  const municipalities = useMemo(() => [...new Set(open.filter((o) => !district || o.district === district).map((o) => o.municipality).filter((x): x is string => !!x))].sort(), [open, district])

  const matches = useCallback((o: Order) => {
    if (filter === "open" && o.dispatch_kind) return false
    if (filter === "fleet" && o.dispatch_kind !== "fleet") return false
    if (filter === "carrier" && o.dispatch_kind !== "carrier") return false
    if (district && o.district !== district) return false
    if (municipality && o.municipality !== municipality) return false
    const t = q.trim().toLowerCase()
    if (t && ![o.source_ref, o.customer_name, o.locality, o.postal_code, o.municipality].some((x) => (x ?? "").toLowerCase().includes(t))) return false
    return true
  }, [filter, district, municipality, q])

  const rows = useMemo(() => open.filter(matches).sort((a, b) => (day10(a.due_date) ?? "9").localeCompare(day10(b.due_date) ?? "9") || a.source_ref.localeCompare(b.source_ref)), [open, matches])
  const pins: MapPin[] = useMemo(
    () => open.map((o) => ({ order: o, color: o.dispatch_kind ? ASSIGNED_COLOR : NONE_COLOR, ring: o.dispatch_kind ? colorOf(o) : null, dim: !matches(o), selected: o.id === selected || multi.has(o.id) })),
    [open, colorOf, matches, selected, multi],
  )
  const counts = useMemo(() => ({
    open: open.filter((o) => !o.dispatch_kind).length,
    fleet: open.filter((o) => o.dispatch_kind === "fleet").length,
    carrier: open.filter((o) => o.dispatch_kind === "carrier").length,
    sageIssues: open.filter((o) => o.sage_status === "failed" || o.sage_status === "no_flow").length,
  }), [open])

  const toggle = (id: string) => setMulti((p) => { const next = new Set(p); if (next.has(id)) next.delete(id); else next.add(id); return next })

  const assign = async (ids: string[], target: DispatchTarget) => {
    setBusy(`A atribuir 0/${ids.length}…`)
    try {
      const r = await dispatchOrders(shell, ids, target, (d, t) => setBusy(`A atribuir ${d}/${t}…`))
      const sageBad = r.done.filter((d) => d.sage_status !== "sent")
      if (r.done.length) {
        shell.toast(
          sageBad.length ? "info" : "success",
          `${r.done.length} atribuída(s)`,
          sageBad.length
            ? `SAGE: ${sageBad.length} por avisar — ${sageBad[0].sage_error ?? "ver na encomenda"}`
            : "SAGE avisado",
        )
      }
      if (r.failed.length) shell.toast("error", `${r.failed.length} não atribuída(s)`, r.failed.slice(0, 3).map((f) => f.error).join(" · "))
      await load()
      setDetailKey((k) => k + 1)
    } catch (err) {
      shell.toast("error", "Não foi possível atribuir", errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const bulk = async () => {
    const ids = [...multi]
    let target: DispatchTarget
    if (bulkTarget === "best") target = "best_carrier"
    else if (bulkTarget === "none") target = { kind: "none" }
    else if (bulkTarget === "fleet") target = { kind: "fleet" }
    else if (bulkTarget.startsWith("v:")) target = { kind: "fleet", vehicle_id: bulkTarget.slice(2) }
    else target = { kind: "carrier", service_id: bulkTarget.slice(2) }
    await assign(ids, target)
    setMulti(new Set())
  }

  if (error) return <div className="p-4"><ErrorBox error={error} /></div>
  if (!orders) return <Loading />
  const current = selected ? open.find((o) => o.id === selected) ?? null : null

  return (
    <div className={cn("grid h-full min-h-[560px] grid-cols-1 gap-3 p-3", current ? "lg:grid-cols-[320px_minmax(0,1fr)_400px]" : "lg:grid-cols-[320px_minmax(0,1fr)]")}>
      {/* ── lista ── */}
      <aside className="flex min-h-0 flex-col rounded-xl border bg-card">
        <div className="flex flex-col gap-2 border-b p-3">
          <div className="flex items-center justify-between">
            <b className="text-[14px]">Encomendas em aberto</b>
            <span className="font-mono text-xs text-muted-foreground">{rows.length}</span>
          </div>
          <input className={inputCls} placeholder="Procurar n.º, cliente, localidade, CP…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="flex flex-wrap gap-1">
            {FILTERS.map(([f, label]) => (
              <button
                key={f}
                className={cn("rounded-full border px-2.5 py-0.5 text-[12px]", filter === f ? "border-accent bg-accent/10 text-foreground" : "text-muted-foreground hover:text-foreground")}
                onClick={() => setFilter(f)}
              >
                {label}{f !== "all" && <span className="ml-1 font-mono text-[11px] opacity-70">{counts[f]}</span>}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <SearchSelect className="text-[12px]" ariaLabel="Distrito" allLabel="Todos os distritos" placeholder="Procurar distrito…" value={district} onChange={(v) => { setDistrict(v); setMunicipality("") }} options={districts.map((d) => ({ value: d, label: d }))} />
            <SearchSelect className="text-[12px]" ariaLabel="Concelho" allLabel="Todos os concelhos" placeholder="Procurar concelho…" value={municipality} onChange={setMunicipality} options={municipalities.map((d) => ({ value: d, label: d }))} />
          </div>
          {counts.sageIssues > 0 && (
            <div className="rounded-md bg-amber-50 px-2 py-1 text-[12px] text-amber-800">{counts.sageIssues} encomenda(s) com o SAGE por avisar — abre-as e carrega em «Repetir».</div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {rows.map((o) => (
            <div
              key={o.id}
              className={cn("grid cursor-pointer grid-cols-[auto_10px_minmax(0,1fr)_auto] items-center gap-2 border-b px-3 py-2 hover:bg-muted/50", selected === o.id && "bg-accent/10")}
              onClick={() => setSelected(o.id)}
            >
              {canEdit ? (
                <input type="checkbox" aria-label={`Selecionar ${o.source_ref}`} checked={multi.has(o.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(o.id)} />
              ) : <span />}
              <span className="h-2.5 w-2.5 rounded-full" style={o.dispatch_kind ? { background: ASSIGNED_COLOR, boxShadow: `0 0 0 2px ${colorOf(o)}` } : { background: NONE_COLOR }} />
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium">{o.locality ?? "—"} · {o.customer_name ?? "—"}</div>
                <div className="truncate font-mono text-[11px] text-muted-foreground">
                  {o.source_ref} · {kg(o.total_kg)} · {o.load_class ?? "—"} · prazo {dm(day10(o.due_date))}
                  {day10(o.due_date) && day10(o.due_date)! < today ? " · atrasada" : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                {o.lat == null || o.lng == null ? <Tag tone="bad">sem local</Tag> : o.dispatch_kind ? <Tag tone="neutral">{o.dispatch_kind === "fleet" ? "frota" : "transp."}</Tag> : null}
                {o.sage_status && o.sage_status !== "sent" && <SageTag o={o} />}
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nada com estes filtros.</div>}
        </div>
        {canEdit && multi.size > 0 && (
          <div className="flex flex-col gap-1.5 border-t p-3">
            <div className="flex items-center justify-between text-[12.5px]">
              <span><b className="font-mono">{multi.size}</b> selecionada(s)</span>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setMulti(new Set())}>limpar</button>
            </div>
            <SearchSelect
              className="text-[12.5px]"
              ariaLabel="Destino"
              placement="top"
              placeholder="Procurar transportadora ou veículo…"
              value={bulkTarget}
              onChange={setBulkTarget}
              options={[
                { value: "best", label: "Transportadora mais barata no prazo" },
                ...[...services.values()].map((s) => ({ value: `s:${s.id}`, label: s.label })),
                { value: "fleet", label: "Frota — escolho o veículo no dia" },
                ...vehicles.map((v) => ({ value: `v:${v.id}`, label: `Frota · ${v.name}` })),
                { value: "none", label: "Retirar atribuição" },
              ]}
            />
            <AsyncButton size="sm" disabled={!!busy} onRun={bulk}>{busy ?? "Atribuir"}</AsyncButton>
          </div>
        )}
      </aside>

      {/* ── mapa ── */}
      <div className="relative min-h-[460px]">
        <DispatchMap shell={shell} pins={pins} warehouse={warehouse} onPick={setSelected} onToggle={(id) => canEdit && toggle(id)} />
        <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-2">
          <span className="pointer-events-auto rounded-lg border bg-card/95 px-2.5 py-1 text-[12px] shadow-sm">
            <b className="font-mono">{counts.open}</b> por atribuir · <b className="font-mono">{counts.fleet}</b> frota · <b className="font-mono">{counts.carrier}</b> transportadora
          </span>
          {canEdit && <span className="pointer-events-auto rounded-lg border bg-card/95 px-2.5 py-1 text-[12px] text-muted-foreground shadow-sm">Shift + clique junta à seleção</span>}
          <Link to="/planeamento" className="pointer-events-auto inline-flex items-center gap-1 rounded-lg border bg-card/95 px-2.5 py-1 text-[12px] text-accent shadow-sm hover:underline"><CalendarRange className="h-3.5 w-3.5" /> Planear o dia</Link>
        </div>
        <div className="absolute bottom-3 left-3 flex max-w-[60%] flex-wrap gap-x-3 gap-y-1 rounded-lg border bg-card/95 px-2.5 py-1.5 text-[11.5px] shadow-sm">
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: NONE_COLOR }} />Por atribuir</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: ASSIGNED_COLOR }} />Atribuída</span>
          <span className="text-muted-foreground">contorno:</span>
          {vehicles.map((v) => <span key={v.id} className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: vehicleColor.get(v.id) }} />{v.name}</span>)}
          {[...new Map([...services.values()].map((s) => [s.carrier_id, s])).values()].map((s) => (
            <span key={s.carrier_id} className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: s.color }} />{s.label.split(" · ")[0]}</span>
          ))}
          <span className="text-muted-foreground">letra = classe de carga</span>
        </div>
      </div>

      {/* ── detalhe: só com uma encomenda aberta (o mapa ocupa o espaço todo sem ela) ── */}
      {current && (
        <aside className="min-h-0 overflow-auto rounded-xl border bg-card">
          <div className="flex flex-col gap-3 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[15px] font-semibold">{current.customer_name ?? "—"}</div>
                <div className="font-mono text-[12px] text-muted-foreground">{current.source_ref} · {current.locality ?? "—"}</div>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSelected(null)} aria-label="Fechar"><X className="h-4 w-4" /></Button>
            </div>
            <AssignBox
              shell={shell}
              order={current}
              vehicles={vehicles}
              vehicleColor={vehicleColor}
              services={services}
              canEdit={canEdit}
              busy={!!busy}
              destLabel={destLabel(current)}
              onAssign={(t) => assign([current.id], t)}
              onResent={load}
            />
            <div className="mt-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Detalhe da encomenda <span className="h-px flex-1 bg-border" />
            </div>
            <OrderDetail key={`${current.id}:${detailKey}`} shell={shell} id={current.id} onChanged={load} hideOptions narrow />
          </div>
        </aside>
      )}
    </div>
  )
}

function AssignBox({
  shell,
  order: o,
  vehicles,
  vehicleColor,
  services,
  canEdit,
  busy,
  destLabel,
  onAssign,
  onResent,
}: {
  shell: AppShell
  order: Order
  vehicles: Vehicle[]
  vehicleColor: Map<string, string>
  services: Map<string, ServiceRef>
  canEdit: boolean
  busy: boolean
  destLabel: string | null
  onAssign: (t: DispatchTarget) => Promise<void>
  onResent: () => Promise<void>
}) {
  const located = o.lat != null && o.lng != null
  const valid = o.options.filter((x) => x.ok && x.cost != null)
  const best = valid.sort((a, b) => (n(a.cost) ?? 0) - (n(b.cost) ?? 0))[0]
  const isCarrier = (sid: string) => o.dispatch_kind === "carrier" && o.carrier_service_id === sid
  return (
    <section className="flex flex-col gap-2.5 rounded-xl border border-green-200 bg-green-50/70 p-3 dark:border-green-900/60 dark:bg-green-950/25" aria-label="Atribuir">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px]">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-green-800 dark:text-green-300">Atribuir</span>
          <span className="mx-1.5 text-muted-foreground">·</span>
          {destLabel ? <b>{destLabel}</b> : <span className="text-muted-foreground">por atribuir</span>}
          {o.pickup_day ? <span className="text-muted-foreground"> · recolha {dm(day10(o.pickup_day))}</span> : null}
        </span>
        <span className="flex items-center gap-1.5">
          <SageTag o={o} />
          {canEdit && o.sage_status && o.sage_status !== "sent" && (
            <AsyncButton size="sm" variant="outline" className="h-6 bg-card px-2 text-[11.5px]" onRun={async () => {
              try {
                const r = await resendSage(shell, o.id)
                shell.toast(r.sage_status === "sent" ? "success" : "error", r.sage_status === "sent" ? "SAGE avisado" : "SAGE por avisar", r.sage_error ?? undefined)
                await onResent()
              } catch (err) {
                shell.toast("error", "Não foi possível avisar o SAGE", errorMessage(err))
              }
            }}><RefreshCw className="h-3 w-3" /> Repetir</AsyncButton>
          )}
        </span>
      </div>
      {o.sage_status && o.sage_status !== "sent" && o.sage_error && <div className="text-[11.5px] text-amber-800">{o.sage_error}</div>}
      {!located && <div className="rounded-md bg-red-50 px-2 py-1 text-[12px] text-red-800">Sem localização: corrige o código postal ou o pino no detalhe abaixo antes de atribuir à frota.</div>}

      {/* ── frota própria ── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground/80">
          <Truck className="h-3.5 w-3.5" /> Frota própria
          <span className="font-normal text-muted-foreground">· o custo sai da rota do dia</span>
        </div>
        <div className={cn("flex flex-wrap gap-1 rounded-lg border bg-card p-2", o.dispatch_kind === "fleet" && "border-green-600 ring-1 ring-green-600/30")}>
          {canEdit ? (
            <>
              {vehicles.map((v) => {
                const noLift = o.needs_tail_lift && !v.has_tail_lift
                const on = o.dispatch_kind === "fleet" && o.dispatch_vehicle_id === v.id
                return (
                  <Button key={v.id} size="sm" variant={on ? "default" : "outline"} className="h-7 gap-1.5 px-2 text-[12px]" disabled={busy || !located || noLift} title={noLift ? "Pede plataforma elevatória" : undefined} onClick={() => void onAssign({ kind: "fleet", vehicle_id: v.id })}>
                    <i className="h-2 w-2 rounded-full" style={{ background: vehicleColor.get(v.id) }} />{v.name}
                  </Button>
                )
              })}
              <Button size="sm" variant={o.dispatch_kind === "fleet" && !o.dispatch_vehicle_id ? "default" : "outline"} className="h-7 px-2 text-[12px]" disabled={busy || !located} onClick={() => void onAssign({ kind: "fleet" })}>
                Veículo escolhido no dia
              </Button>
            </>
          ) : (
            <span className="text-[12px] text-muted-foreground">{vehicles.map((v) => v.name).join(" · ") || "Sem veículos."}</span>
          )}
        </div>
      </div>

      {/* ── transportadoras ── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground/80">
          <Package className="h-3.5 w-3.5" /> Transportadoras
        </div>
        {o.options.length === 0 ? (
          <div className="rounded-lg border bg-card p-2 text-[12px] text-muted-foreground">Sem transportadoras configuradas.</div>
        ) : (
          <div className="flex flex-col divide-y overflow-hidden rounded-lg border bg-card">
            {o.options.map((x) => {
              const on = isCarrier(x.service_id)
              const isBest = best?.service_id === x.service_id
              return (
                <div key={x.service_id} className={cn("grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 px-2.5 py-2", on && "bg-green-50 dark:bg-green-950/40", !x.ok && "opacity-60")}>
                  <i className="h-2.5 w-2.5 rounded-full" style={{ background: services.get(x.service_id)?.color }} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium">
                      {x.carrier_name} <span className="font-normal text-muted-foreground">· {x.service_name}</span>
                      {isBest && <span className="ml-1"><Tag tone="ok">mais barata</Tag></span>}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">{x.ok ? `recolha ${dm(day10(x.pickup))} · chega ${dm(day10(x.arrival))}` : x.why}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-mono text-[13px] font-semibold">{x.cost != null ? eur(x.cost) : "—"}</span>
                    {canEdit && x.ok && (
                      <Button size="sm" variant={on ? "default" : isBest ? "default" : "outline"} className={cn("h-7 px-2.5 text-[12px]", isBest && !on && "bg-green-600 text-white hover:bg-green-700")} disabled={busy || on} onClick={() => void onAssign({ kind: "carrier", service_id: x.service_id })}>
                        {on ? "Atribuída" : "Atribuir"}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {canEdit && o.dispatch_kind && (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" className="h-7 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => void onAssign({ kind: "none" })}>
            <X className="h-3.5 w-3.5" /> Retirar atribuição
          </Button>
        </div>
      )}
    </section>
  )
}
